import _ from 'underscore';
import { datadogLogs } from '~/lib/datadog';
import isFeatureEnabled from '~/components/common/is-feature-enabled';
import EcommerceApi from '../../agent/ecommerce';
import LocalCartUtils from './local-cart-utils';

export default class EcommerceUtils {
  static stripeCheckoutUri(academyOrderItemId, contentType, amountCents, academyPromoId = null) {
    let uri = `/launch_stripe_checkout?academy_order_item_id=${academyOrderItemId}&content_type=${contentType}&amount_cents=${amountCents}`;
    if (academyPromoId !== null) uri += `&academy_promo_id=${academyPromoId}`;

    return uri;
  }

  static async getCart(previouslyFetchedDbCart) {
    if (getExternalUser()) {
      if (_.isEmpty(LocalCartUtils.localStorageCartItems())) {
        return previouslyFetchedDbCart || (await EcommerceApi.fetchCart()).cart;
      }
      const resp = await LocalCartUtils.updateDbCartWithLocalCartItems();
      LocalCartUtils.clearLocalStorageCartItems();
      return resp.cart;
    }
    // guest checkout - not yet supported in stripe-checkout.js
    // eslint-disable-next-line no-undef
    return (await EcommerceApi.createGuestCart(localCartItems)).cart;
  }

  // eslint-disable-next-line consistent-return
  static async addToCart(productId, productType) {
    if (!getExternalUser()) {
      LocalCartUtils.addToLocalStorageCartItems(productId, productType);
    } else {
      return EcommerceApi.addToCart([{ id: productId, type: productType }]);
    }
  }

  // static removeFromCart(productId) {
  //   return EcommerceApi.removeFromCart([{id: productId, type: productType}]);
  // }

  // static isProductInCart(productId, cartItems) {
  //   return _.some(cartItems, cartItem => cartItem.id === productId );
  // }

  static doesContentHavePrice(amountCents, amountProprietaryCurrency) {
    if (!isFeatureEnabled('ecommerce')) return false;

    return (
      (amountCents != null && amountCents > 0) || (amountProprietaryCurrency != null && amountProprietaryCurrency > 0)
    );
  }

  static userNeedsToPurchase(contentId, accessibleContentIds, amountCents, amountProprietaryCurrency) {
    if (!isFeatureEnabled('ecommerce')) return false;

    return (
      this.doesContentHavePrice(amountCents, amountProprietaryCurrency) &&
      accessibleContentIds.indexOf(contentId) === -1
    );
  }

  static currencyFormatter = (currency, amountCents, locale) =>
    new Intl.NumberFormat(locale || 'en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amountCents / 100);

  static formattedPrice = (currency, amountCents, amountProprietaryCurrency) => {
    const doesContentHavePrice = this.doesContentHavePrice(amountCents, amountProprietaryCurrency);
    if (!doesContentHavePrice) return 'Free';

    return amountProprietaryCurrency || this.currencyFormatter(currency, amountCents);
  };

  // TODO: Should we use package? Only support certain currencies?
  static currencyToSymbolMap = {
    USD: '$',
  };

  static getSymbolFromCurrency = (currency) => this.currencyToSymbolMap[currency];

  static formattedPurchaseStatus = (purchaseStatus) => {
    if (purchaseStatus == null) return 'None';
    const purchaseAndRefundStatuses = APP_CONSTANTS.PURCHASE_AND_REFUND_STATUSES;

    switch (purchaseStatus) {
      case purchaseAndRefundStatuses.PENDING_PURCHASE:
        return 'Pending Purchase';
      case purchaseAndRefundStatuses.PURCHASED:
        return 'Purchased';
      case purchaseAndRefundStatuses.REFUND_APPROVED:
        return 'Refund Approved';
      case purchaseAndRefundStatuses.REFUND_REJECTED:
        return 'Refund Rejected';
      case purchaseAndRefundStatuses.REFUND_REQUESTED:
        return 'Refund Requested';
      case purchaseAndRefundStatuses.PARTIALLY_REFUNDED:
        return 'Partially Refunded';
      case purchaseAndRefundStatuses.PARTIALLY_REJECTED:
        return 'Partially Rejected';
      default:
        datadogLogs.logger.info(`EcommerceUtils - Unknown purchase status '${purchaseStatus}'`);

        return 'UNKNOWN';
    }
  };

  static paymentStatus = {
    PROCESSING: 'PROCESSING',
    CONFIRMED: 'CONFIRMED',
    ERROR: 'ERROR',
  };

  static freeOrderStatus = {
    CONFIRMING_FREE_ORDER: 'CONFIRMING_FREE_ORDER',
    FREE_ORDER_CONFIRMED: 'FREE_ORDER_CONFIRMED',
    FREE_ORDER_ERROR: 'FREE_ORDER_ERROR',
  };

  static freeOrderConfirmationStatuses = {
    CONFIRMING_FREE_ORDER: {
      name: 'CONFIRMING_FREE_ORDER',
      msg: 'Confirming Order',
      status: this.freeOrderStatus.CONFIRMING_FREE_ORDER,
    },
    FREE_ORDER_CONFIRMED: {
      name: 'FREE_ORDER_CONFIRMED',
      msg: 'Order Confirmed',
      status: this.freeOrderStatus.FREE_ORDER_CONFIRMED,
    },
    FREE_ORDER_ERROR: {
      name: 'FREE_ORDER_ERROR',
      msg: 'There was an issue confirming your order. Please try again or email support@workramp.com',
      status: this.freeOrderStatus.FREE_ORDER_ERROR,
    },
  };

  static paymentProcessingStatuses = {
    CONFIRMING_PAYMENT: {
      name: 'CONFIRMING_PAYMENT',
      msg: 'Confirming Payment',
      paymentStatus: this.paymentStatus.PROCESSING,
    },
    PAYMENT_INITIATED: {
      name: 'PAYMENT_INITIATED',
      msg: 'Payment Initiated',
      paymentStatus: this.paymentStatus.PROCESSING,
    },
    PAYMENT_CONFIRMED: {
      name: 'PAYMENT_CONFIRMED',
      msg: 'Payment Confirmed',
      paymentStatus: this.paymentStatus.CONFIRMED,
    },
    PAYMENT_ERROR: {
      name: 'PAYMENT_ERROR',
      msg: 'Payment Error',
      paymentStatus: this.paymentStatus.ERROR,
    },
    PROCESSING_LONGER_THAN_NORMAL: {
      name: 'PROCESSING_LONGER_THAN_NORMAL',
      msg: 'Sorry this is taking a while. Still waiting for payment confirmation.',
      paymentStatus: this.paymentStatus.PROCESSING,
    },
    UNABLE_TO_PROCESS: {
      name: 'UNABLE_TO_PROCESS',
      msg: 'There was an issue processing your payment. Please try again or email support@workramp.com',
      paymentStatus: this.paymentStatus.ERROR,
    },
  };

  static stripeValidationErrors = {
    api_connection_error: {
      typeName: 'api_connection_error',
    },
    api_error: {
      typeName: 'api_error',
    },
    authentication_error: {
      typeName: 'authentication_error',
    },
    card_error: {
      typeName: 'card_error',
    },
    idempotency_error: {
      typeName: 'idempotency_error',
    },
    invalid_request_error: {
      typeName: 'invalid_request_error',
    },
    rate_limit_error: {
      typeName: 'rate_limit_error',
    },
    validation_error: {
      typeName: 'validation_error',
    },
  };
}
